import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import { AxiosError } from 'axios';
import i18n from 'i18n';
import { selectUserFranchise } from '../../franchises/ducks/franchises.selector';
import {
  fetchRefereesApi,
  fetchReferralStatsApi,
  fetchReferrersApi,
  getReferralProgramApi,
  upsertReferralProgramApi,
  upsertReferralTiersApi,
  validateReferralApi,
} from '../api';
import { ReferralProgramPayload, ReferralTiersPayload } from '../types';

const tr = (key: string) => i18n.t(`referral.${key}`, { ns: 'referral' });

export const fetchReferralProgramThunk = createAsyncThunk(
  'referral/fetchReferralProgramThunk',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const franchise = selectUserFranchise(getState() as RootState);
    try {
      return await getReferralProgramApi(franchise.id);
    } catch (error: unknown) {
      if ((error as AxiosError).response?.status === 404) {
        return null;
      }
      dispatch(showMessage({ message: tr('errors.loadProgram'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const saveReferralProgramThunk = createAsyncThunk(
  'referral/saveReferralProgramThunk',
  async (payload: ReferralProgramPayload, { dispatch, getState, rejectWithValue }) => {
    const franchise = selectUserFranchise(getState() as RootState);
    try {
      const saved = await upsertReferralProgramApi(franchise.id, payload);
      dispatch(showMessage({ message: tr('config.saveSuccess'), variant: 'success' }));
      return saved;
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('config.saveError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const saveReferralTiersThunk = createAsyncThunk(
  'referral/saveReferralTiersThunk',
  async (payload: ReferralTiersPayload, { dispatch, getState, rejectWithValue }) => {
    const franchise = selectUserFranchise(getState() as RootState);
    try {
      const saved = await upsertReferralTiersApi(franchise.id, payload);
      dispatch(showMessage({ message: tr('tiers.saveSuccess'), variant: 'success' }));
      return saved;
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('tiers.saveError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const fetchReferralStatsThunk = createAsyncThunk(
  'referral/fetchReferralStatsThunk',
  async (_, { dispatch, getState, rejectWithValue }) => {
    const franchise = selectUserFranchise(getState() as RootState);
    try {
      return await fetchReferralStatsApi(franchise.id);
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('errors.loadStats'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const fetchReferrersThunk = createAsyncThunk(
  'referral/fetchReferrersThunk',
  async ({ skip, limit }: { skip: number; limit: number }, { dispatch, getState, rejectWithValue }) => {
    const franchise = selectUserFranchise(getState() as RootState);
    try {
      return await fetchReferrersApi(franchise.id, skip, limit);
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('errors.loadReferrers'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const fetchRefereesThunk = createAsyncThunk(
  'referral/fetchRefereesThunk',
  async (referrerId: string, { dispatch, getState, rejectWithValue }) => {
    const franchise = selectUserFranchise(getState() as RootState);
    try {
      const data = await fetchRefereesApi(franchise.id, referrerId);
      return { referrerId, referees: data.items };
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('errors.loadReferees'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const validateReferralThunk = createAsyncThunk(
  'referral/validateReferralThunk',
  async (
    { referrerId, referralId }: { referrerId: string; referralId: string },
    { dispatch, getState, rejectWithValue },
  ) => {
    const franchise = selectUserFranchise(getState() as RootState);
    try {
      await validateReferralApi(franchise.id, referralId);
      dispatch(showMessage({ message: tr('stats.validateSuccess'), variant: 'success' }));
      // The referrer's reward counter and the referee's status both moved server-side.
      dispatch(fetchRefereesThunk(referrerId));
      dispatch(fetchReferralStatsThunk());
      return { referrerId, referralId };
    } catch (error: unknown) {
      dispatch(showMessage({ message: tr('stats.validateError'), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);
